import * as React from 'react';
import { connect } from 'react-redux';

import navigate from 'libs/router/navigate';

import Spinner from 'components/spinner/Spinner';

import BaseInsightsPage from 'modules/insights/pages/BaseInsightsPage';

import SOVReports from 'modules/insights/sov/components/reports/SOVReports';
import SOVHeaderPage from 'modules/insights/sov/components/header/page/SOVHeaderPage';
import SOVCalloutReport from 'modules/insights/sov/components/callout-v2/report/SOVCalloutReport';

import { danger } from 'redux-store/reducers/common-reducer';
import { trackSOVEvent } from 'modules/insights/sov/tracking/sov-tracking';
import {
  createReport,
  deleteReport,
  duplicateReport,
  fetchReports,
  resetReports
} from 'modules/insights/sov/reducers/reports/collabary/sov-reports';

import { __t, applyTranslations } from 'utils/language';
import { getBrandProfileId } from 'utils/organization';
import { isManagedServiceSubscription } from 'utils/subscription';
import { INSTAGRAM, REPORT, YOUTUBE } from 'modules/insights/sov/utils/sov';

import type { Organization } from 'types';
import type {
  SOVGeneralEventType,
  SOVGeneralProps,
  SOVReport,
  SOVReportByType,
  SOVReportType
} from 'modules/insights/sov/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));

type StateProps = {
  organization: Organization,
  brandProfileId: number | undefined | null,
  reports: Array<SOVReport>,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchReports: (brandProfileId: number) => void,
  dispatchCreateReport: (report: SOVReport) => Promise<any>,
  dispatchDuplicateReport: (report: Partial<SOVReport>) => Promise<any>,
  dispatchDeleteReport: (brandProfileId: number, reportId: number) => Promise<void>,
  dispatchResetReports: () => void,
  dispatchErrorPopup: (message: string) => void
};

type Props = StateProps & DispatchProps;

type State = {
  isActionEvent: boolean
};

const SHARED_MSG_KEY = 'sov_shared';

class SOVReportsPage extends React.PureComponent<Props, State> {
  state = {
    isActionEvent: false
  };

  componentDidMount() {
    const { brandProfileId, dispatchFetchReports } = this.props;

    if (brandProfileId) {
      dispatchFetchReports(brandProfileId);
    }
  }

  componentWillUnmount() {
    const { dispatchResetReports } = this.props;

    dispatchResetReports();
  }

  getDocumentTitle(): string {
    return `${__t(`${SHARED_MSG_KEY}.labels.share_of_voice`)}`;
  }

  getReportsByType(): SOVReportByType {
    const { reports } = this.props;

    return {
      instagram: reports.filter(report => report.reportType === INSTAGRAM),
      youtube: reports.filter(report => report.reportType === YOUTUBE)
    };
  }

  getNewReportProps(props: SOVGeneralProps): SOVReport {
    const { brandProfileId } = this.props;

    return {
      id: -1,
      brandProfileId,
      reportType: props.reportType,
      name: props.name,
      description: props.description,
      channelsInclude: false,
      countries: [],
      createdAt: '',
      updatedAt: '',
      startDate: '',
      endDate: ''
    };
  }

  getDuplicateReportProps(props: SOVGeneralProps): Partial<SOVReport> {
    const { reports } = this.props;
    const selectedReport = reports.find(report => report.id === props.reportId) || {};

    return {
      ...selectedReport,
      name: props.name,
      description: props.description
    };
  }

  handleActionEventToggle = (status: boolean): void => {
    this.setState({
      isActionEvent: status
    });
  };

  handleTrackEvent(
    eventType: SOVGeneralEventType,
    reportId?: number | null,
    reportType?: SOVReportType | null
  ): number {
    const { brandProfileId } = this.props;
    const updatedReportId = reportId || -1;

    trackSOVEvent(eventType, REPORT, reportType, brandProfileId, updatedReportId);

    return updatedReportId;
  }

  handleCreateReport = (props: SOVGeneralProps): void => {
    const { brandProfileId, dispatchCreateReport } = this.props;

    if (brandProfileId) {
      dispatchCreateReport(this.getNewReportProps(props))
        .then(results => this.handleTrackEvent('created', results?.payload?.report.id, props.reportType))
        .then(reportId => navigate.to(`/insights/sov/${String(props.reportType)}/${reportId}`))
        .catch(() => this.handleDispatchErrorPopup('creating', REPORT));
    }
  };

  handleDuplicateReport = (props: SOVGeneralProps): void => {
    const { dispatchDuplicateReport } = this.props;
    const reportId = props.reportId;

    if (reportId) {
      this.handleActionEventToggle(true);

      dispatchDuplicateReport(this.getDuplicateReportProps(props))
        .then(results => {
          this.handleActionEventToggle(false);
          return this.handleTrackEvent('duplicated', results?.payload?.report.id, props.reportType);
        })
        .catch(() => this.handleDispatchErrorPopup('duplicating', REPORT));
    }
  };

  handleDeleteReport = (
    {
      reportId,
      reportType
    }: SOVGeneralProps
  ): void => {
    const { brandProfileId, dispatchDeleteReport } = this.props;

    if (brandProfileId && reportId) {
      this.handleActionEventToggle(true);

      dispatchDeleteReport(brandProfileId, reportId)
        .then(() => {
          this.handleActionEventToggle(false);
          return this.handleTrackEvent('deleted', reportId, reportType);
        })
        .catch(() => this.handleDispatchErrorPopup('deleting', REPORT));
    }
  };

  handleDispatchErrorPopup(actionType: string, itemType: string): void {
    const { dispatchErrorPopup } = this.props;

    this.handleActionEventToggle(false);

    dispatchErrorPopup(
      __t(`${SHARED_MSG_KEY}.messages.failure`, {
        actionType: __t(`${SHARED_MSG_KEY}.labels.${actionType}`),
        itemType: __t(`${SHARED_MSG_KEY}.labels.${itemType}`).toLowerCase()
      })
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    );
  }

  renderReports(reportType: SOVReportType): React.ReactNode {
    const { organization, isLoading } = this.props;
    const { isActionEvent } = this.state;
    const reportsByType = this.getReportsByType();

    return (
      <div className="sov-reports-page__list">
        <SOVHeaderPage
          reportType={reportType}
        />
        <SOVReports
          reportType={reportType}
          organization={organization}
          reports={reportsByType[reportType]}
          isLoading={isLoading}
          isActionEvent={isActionEvent}
          isFreemium={organization?.isFreemium}
          onCreateReport={this.handleCreateReport}
          onDuplicateReport={this.handleDuplicateReport}
          onDeleteReport={this.handleDeleteReport}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { organization, reports, isLoading } = this.props;
    const isManagedService = isManagedServiceSubscription(organization);
    const hasReports = reports.length > 0;
    const hasYouTube = isManagedService && !organization.isFreemium;

    return (
      <BaseInsightsPage
        documentTitle={this.getDocumentTitle()}
        organization={organization}
      >
        <div className="sov-reports-page">
          {isLoading && this.renderSpinner()}
          {!hasReports && !isLoading && (
            <SOVCalloutReport
              hasYouTube={hasYouTube}
              onClick={this.handleCreateReport}
            />
          )}
          {
            hasReports && this.renderReports(INSTAGRAM)
          }
          {
            hasReports && hasYouTube && this.renderReports(YOUTUBE)
          }
        </div>
      </BaseInsightsPage>
    );
  }
}

const mapStateToProps = ({ organizations, sovReports }): StateProps => ({
  organization: organizations.currentOrganization,
  brandProfileId: getBrandProfileId(organizations.currentOrganization),
  reports: sovReports.reports,
  isLoading: sovReports.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchReports: (brandProfileId: number) => (
    dispatch(fetchReports(brandProfileId))
  ),

  dispatchCreateReport: (report: SOVReport) => (
    dispatch(createReport(report))
  ),

  dispatchDuplicateReport: (report: Partial<SOVReport>) => (
    dispatch(duplicateReport(report))
  ),

  dispatchDeleteReport: (brandProfileId: number, reportId: number) => (
    dispatch(deleteReport(brandProfileId, reportId))
  ),

  dispatchResetReports: () => (
    dispatch(resetReports())
  ),

  dispatchErrorPopup: (message: string) => (
    dispatch(danger(message))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVReportsPage);
